import React, { useState, useEffect } from "react";
import { Wallet, History, DollarSign, Loader2, X, Save } from "lucide-react";
import * as XLSX from "xlsx";
import { Wallet as WalletType, WalletHistory } from "../../../../types/features/administration/Wallet";
import useToken from "../../../../hooks/auth/useToken";

import {
  RechargeModal,
  RowActionModal,
  StatusChangeModal,
  WalletStats,
  WalletsTab,
  HistoryTab,
} from "../../../../components/features/administration/wallets";
import { FilterState, HistoryStatusFilter } from "../../../../types/features/administration/Wallet";
import { walletService } from "../../../../services/administration/walletService";
import useCountries from "../../../../hooks/administration/countries/useCountries";
import useWallets from "../../../../hooks/administration/wallets/useWallets";

const WalletsPage: React.FC = () => {
  const {
    wallets,
    histories,
    walletsLoading,
    historyLoading,
    rechargeWallet,
    debitWallet,
    updateDailyLimit,
    getHistories,
    getWallets,
    searchWallets,
    searchHistories,
    getStats,
    stats,
    statsLoading,
    walletsPagination,
    historiesPagination,
    updateTransactionStatus,
  } = useWallets({});
  const { countries } = useCountries({});
  const { token } = useToken();

  const [activeTab, setActiveTab] = useState<"wallets" | "history">("wallets");
  const [filters, setFilters] = useState<FilterState>({ search: "" });
  const [historyFilters, setHistoryFilters] = useState<FilterState>({ search: "" });
  const [historyTypeFilter, setHistoryTypeFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [walletSortBy, setWalletSortBy] = useState<string>("name");
  const [walletSortOrder, setWalletSortOrder] = useState<string>("asc");
  const [historySortBy, setHistorySortBy] = useState<string>("created_at");
  const [historySortOrder, setHistorySortOrder] = useState<string>("desc");
  const [historyStartDate, setHistoryStartDate] = useState<string>("");
  const [historyEndDate, setHistoryEndDate] = useState<string>("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState<HistoryStatusFilter>("");
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showDebitModal, setShowDebitModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNegativeOnly, setShowNegativeOnly] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [rowActionWallet, setRowActionWallet] = useState<WalletType | null>(null);
  const [rowActionType, setRowActionType] = useState<"recharge" | "debit">("recharge");
  const [limitWallet, setLimitWallet] = useState<WalletType | null>(null);
  const [limitValue, setLimitValue] = useState("");
  const [isLimitSaving, setIsLimitSaving] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  const [isExportingWallets, setIsExportingWallets] = useState(false);
  const [isExportingHistory, setIsExportingHistory] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    if (token) {
      getStats();
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "wallets") {
      if (filters.search) {
        searchWallets(filters.search, currentPage, itemsPerPage, walletSortBy, walletSortOrder);
      } else {
        getWallets(currentPage, itemsPerPage, undefined, walletSortBy, walletSortOrder, showNegativeOnly);
      }
    }
  }, [currentPage, activeTab, filters.search, walletSortBy, walletSortOrder, showNegativeOnly]);

  useEffect(() => {
    if (activeTab === "history") {
      const start = historyStartDate || undefined;
      const end = historyEndDate || undefined;
      const status = historyStatusFilter || undefined;

      if (historyFilters.search) {
        searchHistories(
          historyFilters.search,
          historyPage,
          50,
          historyTypeFilter || undefined,
          historySortBy,
          historySortOrder,
          start,
          end,
          status
        );
      } else {
        getHistories(
          historyPage,
          50,
          undefined,
          undefined,
          historyTypeFilter || undefined,
          historySortBy,
          historySortOrder,
          start,
          end,
          status
        );
      }
    }
  }, [
    historyPage,
    activeTab,
    historyTypeFilter,
    historyFilters.search,
    historySortBy,
    historySortOrder,
    historyStartDate,
    historyEndDate,
    historyStatusFilter,
  ]);

  const filteredWallets = wallets || [];

  const handleWalletSort = (sortBy: string) => {
    if (walletSortBy === sortBy) {
      setWalletSortOrder(walletSortOrder === "asc" ? "desc" : "asc");
    } else {
      setWalletSortBy(sortBy);
      setWalletSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handleHistorySort = (sortBy: string) => {
    if (historySortBy === sortBy) {
      setHistorySortOrder(historySortOrder === "asc" ? "desc" : "asc");
    } else {
      setHistorySortBy(sortBy);
      setHistorySortOrder("asc");
    }
    setHistoryPage(1);
  };

  const filteredHistories = histories || [];

  const totalWalletPages = walletsPagination?.total_pages || 1;
  const currentWallets = filteredWallets;
  const totalHistoryPages = historiesPagination?.total_pages || 1;
  const currentHistories = filteredHistories;

  const totalByDevise = stats?.total_by_devise || {};
  const totalRechargedByDevise = stats?.total_recharged_by_devise || {};
  const totalDebitedByDevise = stats?.total_debited_by_devise || {};

  // Nouveaux totaux journaliers - Recharges
  const totalRechargedPendingByDevise = stats?.total_recharged_pending_today_by_devise || {};
  const totalRechargedCompletedByDevise = stats?.total_recharged_completed_today_by_devise || {};
  const totalRechargedCancelledByDevise = stats?.total_recharged_cancelled_today_by_devise || {};

  // Nouveaux totaux journaliers - Débits
  const totalDebitedPendingByDevise = stats?.total_debited_pending_today_by_devise || {};
  const totalDebitedCompletedByDevise = stats?.total_debited_completed_today_by_devise || {};
  const totalDebitedCancelledByDevise = stats?.total_debited_cancelled_today_by_devise || {};

  const exportWalletsToExcel = async () => {
    setIsExportingWallets(true);
    try {
      const pageSize = 100;
      let page = 1;
      let totalPages = 1;
      const all: any[] = [];
      do {
        const data = filters.search
          ? await walletService.searchWallets(token, filters.search, page, pageSize, walletSortBy, walletSortOrder)
          : await walletService.getWallets(
              token,
              page,
              pageSize,
              undefined,
              walletSortBy,
              walletSortOrder,
              showNegativeOnly
            );
        all.push(...(data.wallets || []));
        totalPages = data.pagination?.total_pages || 1;
        page += 1;
      } while (page <= totalPages);

      if (all.length === 0) {
        alert("Aucun wallet à exporter");
        return;
      }

      const rows = all.map((w) => ({
        Utilisateur: `${w.user?.first_name || ""} ${w.user?.last_name || ""}`.trim(),
        Email: w.user?.email || "",
        Téléphone: w.user?.phonenumber || "",
        Pays: w.country?.name || "",
        Code: w.country?.code || "",
        Indicatif: w.country?.phonecode ? `+${w.country.phonecode}` : "",
        Devise: w.country?.devise || "",
        "Total rechargé": w.total_recharged ?? 0,
        "Total débité": w.total_debited ?? 0,
        Solde: w.balance ?? 0,
        "Mis à jour": w.updated_at ? new Date(w.updated_at).toLocaleString("fr-FR") : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Wallets");
      XLSX.writeFile(workbook, `wallets_${new Date().toISOString().split("T")[0]}.xlsx`);
    } catch (error) {
      console.error(error);
      alert("Erreur lors de l'export des wallets");
    } finally {
      setIsExportingWallets(false);
    }
  };

  const exportHistoryToExcel = async () => {
    setIsExportingHistory(true);
    try {
      const pageSize = 100;
      let page = 1;
      let totalPages = 1;
      const all: WalletHistory[] = [];
      const start = historyStartDate || undefined;
      const end = historyEndDate || undefined;
      const status = historyStatusFilter || undefined;

      do {
        const data = historyFilters.search
          ? await walletService.searchWalletHistories(
              token,
              historyFilters.search,
              page,
              pageSize,
              historyTypeFilter || undefined,
              historySortBy,
              historySortOrder,
              start,
              end,
              status
            )
          : await walletService.getWalletHistories(
              token,
              page,
              pageSize,
              undefined,
              undefined,
              historyTypeFilter || undefined,
              historySortBy,
              historySortOrder,
              start,
              end,
              status
            );
        all.push(...(data.histories || []));
        totalPages = data.pagination?.total_pages || 1;
        page += 1;
      } while (page <= totalPages);

      if (all.length === 0) {
        alert("Aucun historique à exporter");
        return;
      }

      const rows = all.map((h) => ({
        Type: h.type === "RECHARGE" ? "Recharge" : "Débit",
        Statut: h.status === "COMPLETED" ? "Complété" : h.status === "CANCELLED" ? "Annulé" : "En attente",
        Utilisateur: `${h.user?.first_name || ""} ${h.user?.last_name || ""}`.trim(),
        Email: h.user?.email || "",
        Pays: h.country?.name || "",
        Devise: h.country?.devise || "",
        Montant: h.amount ?? 0,
        "Solde avant": h.account_before ?? "",
        "Solde après": h.account_after ?? "",
        Description: h.description || "",
        Date: h.created_at ? new Date(h.created_at).toLocaleString("fr-FR") : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Historique");
      XLSX.writeFile(workbook, `historique_wallets_${new Date().toISOString().split("T")[0]}.xlsx`);
    } catch (error) {
      console.error(error);
      alert("Erreur lors de l'export de l'historique");
    } finally {
      setIsExportingHistory(false);
    }
  };

  const handleRecharge = async (data: {
    user_id: string;
    country_id: number;
    amount: number;
    description?: string;
  }) => {
    setIsActionLoading(true);
    try {
      await rechargeWallet(data);
      setShowRechargeModal(false);
      setRowActionWallet(null);
      getWallets(currentPage, itemsPerPage, undefined, walletSortBy, walletSortOrder, showNegativeOnly);
      getStats();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || "Erreur lors de la recharge");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDebit = async (data: { user_id: string; country_id: number; amount: number; description?: string }) => {
    setIsActionLoading(true);
    try {
      await debitWallet(data);
      setShowDebitModal(false);
      setRowActionWallet(null);
      getWallets(currentPage, itemsPerPage, undefined, walletSortBy, walletSortOrder, showNegativeOnly);
      getStats();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || "Erreur lors du débit");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleRowAction = async (data: {
    user_id: string;
    country_id: number;
    amount: number;
    description?: string;
  }) => {
    if (rowActionType === "recharge") {
      await handleRecharge(data);
    } else {
      await handleDebit(data);
    }
  };

  const handleCreateWallet = async (data: {
    user_id: string;
    country_id: number;
    amount: number;
    description?: string;
  }) => {
    setIsActionLoading(true);
    try {
      await rechargeWallet({ ...data, description: data.description || "Création de wallet" });
      setShowCreateModal(false);
      getWallets(currentPage, itemsPerPage, undefined, walletSortBy, walletSortOrder, showNegativeOnly);
      getStats();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || "Erreur lors de la création du wallet");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStatusChange = async (status: "COMPLETED" | "CANCELLED" | "PENDING") => {
    if (!selectedTransaction) return;

    setIsStatusUpdating(true);
    try {
      await updateTransactionStatus(selectedTransaction.id, selectedTransaction.wallet_id, status);
      setShowStatusModal(false);
      setSelectedTransaction(null);

      const start = historyStartDate || undefined;
      const end = historyEndDate || undefined;
      const statusParam = historyStatusFilter || undefined;

      if (historyFilters.search) {
        searchHistories(
          historyFilters.search,
          historyPage,
          50,
          historyTypeFilter || undefined,
          historySortBy,
          historySortOrder,
          start,
          end,
          statusParam
        );
      } else {
        getHistories(
          historyPage,
          50,
          undefined,
          undefined,
          historyTypeFilter || undefined,
          historySortBy,
          historySortOrder,
          start,
          end,
          statusParam
        );
      }
      getStats();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || "Erreur lors du changement de statut");
    } finally {
      setIsStatusUpdating(false);
    }
  };

  return (
    <div className="p-2 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Wallet className="w-8 h-8 mt-2 text-green-600" />
            Wallets
          </h1>
          <p className="text-gray-600 mt-1">Gestion des portefeuilles utilisateurs</p>
        </div>
      </div>

      <WalletStats
        stats={stats}
        statsLoading={statsLoading}
        totalByDevise={totalByDevise}
        totalRechargedByDevise={totalRechargedByDevise}
        totalDebitedByDevise={totalDebitedByDevise}
        totalRechargedPendingByDevise={totalRechargedPendingByDevise}
        totalRechargedCompletedByDevise={totalRechargedCompletedByDevise}
        totalRechargedCancelledByDevise={totalRechargedCancelledByDevise}
        totalDebitedPendingByDevise={totalDebitedPendingByDevise}
        totalDebitedCompletedByDevise={totalDebitedCompletedByDevise}
        totalDebitedCancelledByDevise={totalDebitedCancelledByDevise}
      />

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => {
              setActiveTab("wallets");
              setCurrentPage(1);
            }}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === "wallets" ? "text-green-600 border-b-2 border-green-600 bg-green-50" : "text-gray-500 hover:text-gray-700"}`}
          >
            <DollarSign className="w-4 h-4" /> Wallets ({walletsPagination?.total || 0})
          </button>
          <button
            onClick={() => {
              setActiveTab("history");
              setHistoryPage(1);
            }}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === "history" ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50" : "text-gray-500 hover:text-gray-700"}`}
          >
            <History className="w-4 h-4" /> Historique ({historiesPagination?.total || 0})
          </button>
        </div>

        {activeTab === "wallets" && (
          <WalletsTab
            wallets={currentWallets}
            walletsLoading={walletsLoading}
            walletsPagination={walletsPagination}
            filters={filters}
            setFilters={setFilters}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalWalletPages={totalWalletPages}
            walletSortBy={walletSortBy}
            walletSortOrder={walletSortOrder}
            handleWalletSort={handleWalletSort}
            showNegativeOnly={showNegativeOnly}
            setShowNegativeOnly={setShowNegativeOnly}
            exportWalletsToExcel={exportWalletsToExcel}
            isExportingWallets={isExportingWallets}
            setShowCreateModal={setShowCreateModal}
            setRowActionWallet={setRowActionWallet}
            setRowActionType={setRowActionType}
            onEditDailyLimit={(wallet) => {
              setLimitWallet(wallet);
              setLimitValue(String(wallet.daily_limit ?? 0));
            }}
          />
        )}

        {activeTab === "history" && (
          <HistoryTab
            histories={currentHistories}
            historyLoading={historyLoading}
            historiesPagination={historiesPagination}
            historyFilters={historyFilters}
            setHistoryFilters={setHistoryFilters}
            historyPage={historyPage}
            setHistoryPage={setHistoryPage}
            totalHistoryPages={totalHistoryPages}
            historyTypeFilter={historyTypeFilter}
            setHistoryTypeFilter={setHistoryTypeFilter}
            historySortBy={historySortBy}
            historySortOrder={historySortOrder}
            handleHistorySort={handleHistorySort}
            exportHistoryToExcel={exportHistoryToExcel}
            isExportingHistory={isExportingHistory}
            setSelectedTransaction={setSelectedTransaction}
            setShowStatusModal={setShowStatusModal}
            historyStartDate={historyStartDate}
            setHistoryStartDate={setHistoryStartDate}
            historyEndDate={historyEndDate}
            setHistoryEndDate={setHistoryEndDate}
            historyStatusFilter={historyStatusFilter}
            setHistoryStatusFilter={setHistoryStatusFilter}
          />
        )}
      </div>

      <RechargeModal
        countries={countries || []}
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        onConfirm={handleRecharge}
        isLoading={isActionLoading}
      />

      <RechargeModal
        countries={countries || []}
        isOpen={showDebitModal}
        onClose={() => setShowDebitModal(false)}
        onConfirm={handleDebit}
        isLoading={isActionLoading}
        isDebit={true}
      />

      <RechargeModal
        countries={countries || []}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onConfirm={handleCreateWallet}
        isLoading={isActionLoading}
        isCreate={true}
      />

      <RowActionModal
        isOpen={rowActionWallet !== null}
        onClose={() => setRowActionWallet(null)}
        onConfirm={handleRowAction}
        isLoading={isActionLoading}
        isDebit={rowActionType === "debit"}
        wallet={rowActionWallet}
      />

      {limitWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">Limite journalière</h2>
              <button type="button" onClick={() => setLimitWallet(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {limitWallet.user?.first_name} {limitWallet.user?.last_name} · {limitWallet.country?.name} (
              {limitWallet.country?.devise})
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant max / jour ({limitWallet.country?.devise || "—"})
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={limitValue}
              onChange={(e) => setLimitValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 mb-4"
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setLimitWallet(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isLimitSaving}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={isLimitSaving}
                onClick={async () => {
                  const parsed = Number(String(limitValue).replace(",", "."));
                  if (Number.isNaN(parsed) || parsed < 0) {
                    alert("Montant invalide");
                    return;
                  }
                  if (!limitWallet.id) return;
                  setIsLimitSaving(true);
                  try {
                    await updateDailyLimit(String(limitWallet.id), parsed);
                    setLimitWallet(null);
                  } catch (error) {
                    console.error(error);
                    alert("Erreur lors de la mise à jour de la limite");
                  } finally {
                    setIsLimitSaving(false);
                  }
                }}
                className="flex-1 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isLimitSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedTransaction(null);
        }}
        onConfirm={handleStatusChange}
        isLoading={isStatusUpdating}
        currentStatus={selectedTransaction?.status || "PENDING"}
        transaction={selectedTransaction}
      />
    </div>
  );
};

export default WalletsPage;
